// ---------------------------------------------------------------------------
// Entering a weight, a count, or a money amount.
//
// This is the screen a cashier touches most, so it is built for a finger:
// 64 px keys, a large read-out, and the running line total visible while
// typing so a mistake is obvious before it reaches the cart.
//
// A product sold by the kilogram can be entered in kilograms or in grams.
// Typing 1250 in grams shows "that is 1.250 kg" underneath and stores the
// kilogram figure, so the cart, the receipt and the eventual sale all speak
// one unit.
//
// TWO STEPS, ONE DIALOG. A product with types - طاووق متبل as أحمر, مدخن or
// أبيض - opens on the type first, and only then on the keypad. It is the SAME
// dialog changing state, not a second one stacked on top: a modal over a
// modal on a touchscreen is how a cashier ends up dismissing the wrong thing
// mid-rush. Back returns to the types without closing anything.
//
// There is no skip and no default. Even with a single type on offer, it is
// chosen explicitly - the parent product is not something anybody can buy,
// and a silent auto-selection is exactly how the wrong meat gets marinated.
//
// LIRA, AND ONLY LIRA. Every price a cashier reads on this dialog is in lira:
// the shop is paid in lira, the customer asks in lira, and a dollar figure
// beside it is one more number to misread mid-rush. The RECEIPT is different
// on purpose - it keeps a small dollar figure for the owner - and that
// difference is deliberate, not an oversight.
//
// The dollar price is shown here in exactly one case: the shop has no
// exchange rate set at all. Then it is the only true number there is, and a
// button with no price on it would be worse.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Delete, Scale } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { useProductAvailability } from '../../api/kitchenTickets.api';
import {
  formatLbp,
  formatQuantity,
  formatUsd,
  toDecimal,
  unitLabel,
  usdToLbpDisplay,
} from '../../utils/format';
import {
  checkQuantity,
  defaultModeFor,
  normaliseWeightEntry,
  type CartLine,
  type QuantityMode,
} from '../../stores/cartStore';
import { optionLabelText, optionLine } from '../../utils/optionLabel';
import { quotedUnitPrice, unitPriceInLbp } from '../../stores/cartStore';
import type { Product, ProductVariant } from '../../types';

interface QuantityKeypadProps {
  open: boolean;
  /// Adding a new line.
  product: Product | null;
  /// Editing an existing line. When set, `product` is ignored.
  editingLine: CartLine | null;
  /// The catalogue product behind the line being edited, so the cashier can
  /// change the type. Absent when the product is no longer in the catalogue,
  /// in which case the line keeps the type it has.
  editingProduct?: Product | null;
  /// The shop's USD to LBP rate, from the same query the header and the cart
  /// read. Null when none has been set, which is a normal state on a new
  /// install rather than an error.
  exchangeRate?: string | null;
  onClose: () => void;
  onSubmit: (result: {
    quantity: string;
    mode: QuantityMode;
    customAmount?: string;
    note: string;
    /// The type chosen in the first step, when the product has types.
    variant?: ProductVariant | null;
  }) => void;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'];

/// A parent for quoting an option on its own: an option's quote never
/// reads the parent, so nothing here is ever looked at.
const NO_PARENT = { sellingPrice: null, sellingPriceLbp: null, promotion: null };

export function QuantityKeypad({
  open,
  product,
  editingLine,
  editingProduct,
  exchangeRate = null,
  onClose,
  onSubmit,
}: QuantityKeypadProps) {
  const { t, locale, format } = useI18n();

  /// A price, in the money the shop set it in.
  ///
  /// THE EXACT LIRA when there is one - the figure the owner typed, shown to
  /// the pound. Only a price recorded before exact lira existed is converted,
  /// and only then, which is how those products always read.
  const money = (lbp: string | null | undefined, usd: Parameters<typeof toDecimal>[0]) => {
    if (lbp) return formatLbp(lbp, locale);
    return exchangeRate ? usdToLbpDisplay(usd, exchangeRate, locale) : formatUsd(usd);
  };

  /// The product this dialog is about, whether adding or editing.
  const subject = product ?? editingProduct ?? null;

  /// The types on offer. Switched-off and retired ones never reach here - the
  /// server does not send them - so anything in this list is sellable now.
  const offered = useMemo(
    () => (subject?.hasVariants ? (subject.variants ?? []).filter((row) => row.isActive) : []),
    [subject],
  );
  const needsVariant = offered.length > 0;

  /// The type chosen in this dialog. Starts as the line's own when editing,
  /// so opening an existing line shows what was picked.
  const [variant, setVariant] = useState<ProductVariant | null>(null);
  /// True while the cashier is choosing a type rather than entering a
  /// quantity. Same dialog, different step.
  const [choosing, setChoosing] = useState(false);

  // The line being edited wins; otherwise it is a new line for `product`.
  const unit = editingLine?.sellingUnit ?? product?.sellingUnit ?? 'PIECE';
  //
  // THE ONE QUOTE. A new line, or a line whose type is being changed, is
  // priced by `quotedUnitPrice` - the same rule the card and the cart use,
  // so this panel previews the EFFECTIVE price (a discount already applied)
  // and can never show a figure the order will not charge. A line being
  // edited with its type unchanged keeps the figures it was quoted at.
  const quote =
    variant && subject && variant.id !== editingLine?.variantId
      ? quotedUnitPrice(subject, variant)
      : editingLine
        ? null
        : subject
          ? quotedUnitPrice(subject, null)
          : null;
  const unitPrice = quote ? quote.unitPriceUsd : (editingLine?.unitPrice ?? '0');
  /// The same price in lira, when it was set in lira.
  const unitPriceLbp = quote ? quote.unitPriceLbp : (editingLine?.unitPriceLbp ?? null);
  /// The discount behind the figure, when there is one - shown so the
  /// cashier can answer "why 960,000?" without leaving the panel.
  const discount =
    quote?.promotion && quote.promotion.promotionType !== 'NONE'
      ? { basePriceLbp: quote.promotion.basePriceLbp, percent: quote.promotion.discountPercent }
      : !quote && editingLine && editingLine.promotionType && editingLine.promotionType !== 'NONE' && editingLine.basePriceLbp
        ? { basePriceLbp: editingLine.basePriceLbp, percent: editingLine.discountPercent ?? null }
        : null;

  /// WHAT ONE OF THESE COSTS, in whole lira - the one figure this dialog both
  /// shows and multiplies. `unitPriceInLbp` is the same rule the cart and the
  /// server use, so the preview cannot disagree with the order it previews.
  ///
  /// It used to disagree: the price above was displayed by converting the
  /// dollars and rounding the lira, while the total below rounded the dollars
  /// to cents and converted THAT. One kilo of kafta read 1,400,004 in one line
  /// and 1,399,780 in the next.
  const unitLbp = exchangeRate ? unitPriceInLbp(unitPriceLbp, unitPrice, exchangeRate) : null;
  const name = editingLine
    ? locale === 'ar'
      ? editingLine.nameAr
      : editingLine.nameEn
    : locale === 'ar'
      ? (product?.nameAr ?? '')
      : (product?.nameEn ?? '');

  const variantName = variant ? (locale === 'ar' ? variant.nameAr : variant.nameEn) : null;

  // WHAT is being chosen, in this product's own words. Chips are chosen by
  // size, tawook by type, a sauce by flavour - and a product that predates
  // options says nothing, which reads as a type.
  //
  // Taken from the PRODUCT, not from the line: this heading is the question
  // being asked right now. What a completed sale says is a different fact,
  // snapshotted at the time, and read from the document instead.
  const optionLabel = optionLabelText(
    { ar: subject?.optionLabelAr, en: subject?.optionLabelEn },
    locale,
  );

  const mode: QuantityMode = editingLine?.quantityMode ?? defaultModeFor(unit);

  const [entry, setEntry] = useState('');
  const [entryUnit, setEntryUnit] = useState<'kg' | 'g'>('kg');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset every time the dialog opens, so a previous entry never leaks into
  // the next product.
  useEffect(() => {
    if (!open) return;

    setTouched(false);
    setEntryUnit('kg');

    // Which step this dialog opens on. A product with types opens on the
    // types - always, including when only one is offered, and including when
    // an existing line is being edited and the cashier may want a different
    // one. An ordinary product goes straight to the keypad, unchanged.
    const chosen = editingLine?.variantId
      ? ((subject?.variants ?? []).find((row) => row.id === editingLine.variantId) ?? {
          // The line's own snapshot, for a type that has since been
          // withdrawn: the cashier can see what it was and pick again.
          id: editingLine.variantId,
          nameEn: editingLine.variantNameEn ?? '',
          nameAr: editingLine.variantNameAr ?? '',
          sellingPrice: editingLine.unitPrice,
          isActive: false,
          sortOrder: 0,
        })
      : null;

    setVariant(chosen);
    setChoosing(Boolean(subject?.hasVariants) && (subject?.variants ?? []).some((row) => row.isActive)
      ? !editingLine || !chosen
      : false);

    if (editingLine) {
      // EDITING KEEPS WHAT IS THERE. A line of 7 packs opens on 7; the empty
      // start below is for a NEW item only.
      setEntry(
        editingLine.quantityMode === 'CUSTOM_AMOUNT'
          ? (editingLine.customAmount ?? '')
          : editingLine.quantity,
      );
      setNote(editingLine.note ?? '');
    } else {
      // EMPTY, WHATEVER THE UNIT - the read-out shows 0 and the first digit
      // typed becomes the whole number.
      //
      // A counted item used to start at 1. It reads as helpful and is not:
      // a cashier ringing up 25 packs types 2 and gets 12, or has to clear
      // the 1 first, forty times a day. Starting empty means 2 then 5 is 25,
      // and Add to the order stays disabled until a real quantity exists -
      // so nothing can be added by pressing the button on the way past.
      setEntry('');
      setNote('');
    }
    // `subject` is read to decide the opening step; it does not change while
    // the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingLine, mode]);

  const press = (key: string) => {
    setTouched(true);
    setEntry((current) => {
      if (key === '.') {
        if (mode === 'COUNT') return current; // counts are whole numbers
        if (current.includes('.')) return current;
        return current.length === 0 ? '0.' : `${current}.`;
      }

      // Stop somebody leaning on a key and producing a nonsense number.
      if (current.replace('.', '').length >= 9) return current;

      // A leading zero is replaced rather than appended: 0 then 5 is 5, not 05.
      if (current === '0') return key;
      return current + key;
    });
  };

  const backspace = () => {
    setTouched(true);
    setEntry((current) => current.slice(0, -1));
  };

  const clearEntry = () => {
    setTouched(true);
    setEntry('');
  };

  /// The value in the cart's own unit: kilograms for weights.
  const normalised = useMemo(() => {
    if (entry.trim().length === 0) return '';
    if (mode === 'WEIGHT_KG') return normaliseWeightEntry(entry, entryUnit);
    return entry;
  }, [entry, entryUnit, mode]);

  const check = checkQuantity(entry.trim().length === 0 ? '' : normalised, mode);

  const problemMessage =
    !touched || check.ok
      ? null
      : check.problem === 'EMPTY'
        ? t.pos.errorEmpty
        : check.problem === 'NOT_A_NUMBER'
          ? t.pos.errorNotANumber
          : check.problem === 'ZERO_OR_LESS'
            ? t.pos.errorZero
            : check.problem === 'MUST_BE_WHOLE'
              ? t.pos.errorWholeNumber
              : t.pos.errorTooLarge;

  /// Running line total, so a mis-typed weight is obvious before adding.
  ///
  /// Worked out in LIRA when the price is in lira - the same arithmetic the
  /// cart and the server do, so what the cashier is shown here is what the
  /// customer is asked for, to the pound.
  const previewTotal = useMemo(() => {
    if (!check.ok) return null;
    if (mode === 'CUSTOM_AMOUNT') {
      // "Give me ten dollars of this": the money is the input, so this is the
      // one line that genuinely starts in dollars.
      return { lbp: null, usd: toDecimal(normalised).toDecimalPlaces(2) };
    }
    return {
      // ONE rounding boundary, at the end, on the SAME unit price shown above.
      lbp: unitLbp ? unitLbp.times(toDecimal(normalised)).toDecimalPlaces(0).toFixed(0) : null,
      // Dollars DERIVED from the lira line, never the other way round - so a
      // discounted kilo's dollars are the discounted lira divided once.
      usd:
        unitLbp && exchangeRate
          ? unitLbp.times(toDecimal(normalised)).toDecimalPlaces(0).dividedBy(toDecimal(exchangeRate)).toDecimalPlaces(2)
          : toDecimal(unitPrice).times(toDecimal(normalised)).toDecimalPlaces(2),
    };
  }, [check.ok, mode, normalised, unitPrice, unitLbp, exchangeRate]);

  const title =
    mode === 'WEIGHT_KG' ? t.pos.enterWeight : mode === 'CUSTOM_AMOUNT' ? t.pos.enterAmount : t.pos.enterQuantity;

  // What is genuinely sellable, as opposed to what is on the shelf.
  //
  // Asked only while this dialog is open, and shown only when something is
  // actually reserved: on an ordinary day nothing is, and a line saying
  // "0 reserved" on every product would be noise a cashier learns to ignore.
  const { data: availability } = useProductAvailability(
    product?.id ?? null,
    open && !editingLine,
  );
  const hasReservations = availability
    ? toDecimal(availability.reserved).greaterThan(0)
    : false;

  const submit = () => {
    setTouched(true);
    if (!check.ok) return;

    onSubmit({
      quantity: mode === 'CUSTOM_AMOUNT' ? '1' : normalised,
      mode,
      ...(mode === 'CUSTOM_AMOUNT' ? { customAmount: normalised } : {}),
      note,
      variant,
    });
  };

  // A product that needs a type but has none on offer CANNOT BE SOLD. It
  // should not be savable in that state at all, but a till running from a
  // stale cache can still meet one - and falling through to the keypad here
  // would sell the parent at a price that is not the price of anything.
  if (subject?.hasVariants && offered.length === 0) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={name}
        size="sm"
        guardUnsaved
        footer={
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t.common.cancel}
          </button>
        }
      >
        <p className="text-sm text-ink" role="alert">
          {t.products.variantUnavailable}
        </p>
      </Modal>
    );
  }

  // --- step one: which type -------------------------------------------------
  //
  // The whole dialog, not a layer on top of it. The keypad below does not
  // exist yet as far as the cashier is concerned, which is the point: there
  // is no weight to enter until the shop knows what it is weighing.
  if (choosing) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={format(t.products.chooseOption, { label: optionLabel })}
        description={name}
        size="sm"
        guardUnsaved
        footer={
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t.common.cancel}
          </button>
        }
      >
        <ul className="space-y-2">
          {offered.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-control border border-line-strong bg-surface px-4 py-3 text-start transition-colors hover:bg-surface-sunken"
                onClick={() => {
                  setVariant(option);
                  setChoosing(false);
                }}
              >
                <span className="min-w-0 flex-1 truncate text-base font-semibold text-ink">
                  {locale === 'ar' ? option.nameAr : option.nameEn}
                </span>
                <span className="numeric shrink-0 text-sm text-ink-muted">
                  {/* Each type at ITS effective price - the same quote the
                      panel and the cart will use once it is chosen. */}
                  {money(
                    exchangeRate
                      ? unitPriceInLbp(
                          quotedUnitPrice(subject ?? NO_PARENT, option).unitPriceLbp,
                          option.sellingPrice,
                          exchangeRate,
                        ).toFixed(0)
                      : quotedUnitPrice(subject ?? NO_PARENT, option).unitPriceLbp,
                    option.sellingPrice,
                  )}{' '}
                  / {unitLabel(unit, locale)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={variantName ? `${name} - ${variantName}` : name}
      size="sm"
      guardUnsaved
      footer={
        <>
          {/* Back to the types, WITHOUT closing the dialog: a cashier who
              picked the wrong one should not have to start again. */}
          {needsVariant ? (
            <button type="button" className="btn-secondary" onClick={() => setChoosing(true)}>
              {locale === 'ar' ? (
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              )}
              {t.common.back}
            </button>
          ) : (
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t.common.cancel}
            </button>
          )}
          <button type="button" className="btn-primary" onClick={submit} disabled={!check.ok}>
            {editingLine ? t.pos.saveChanges : t.pos.addToOrder}
          </button>
        </>
      }
    >
      {/* The chosen type, kept in front of the cashier while they weigh. */}
      {variantName ? (
        <p className="mb-3 flex items-center justify-between gap-2 rounded-control border border-butcher-green/30 bg-butcher-green/5 px-3 py-2 text-sm">
          {/* "الحجم: كبير" rather than a bare "كبير" - which of two sizes is
              the operational half, and a cashier glancing back at this while
              they weigh needs to see what they answered, not just the answer. */}
          <span className="font-semibold text-ink">
            {optionLine(
              { ar: subject?.optionLabelAr, en: subject?.optionLabelEn },
              variantName,
              locale,
            )}
          </span>
          <span className="numeric text-ink-muted">
            {money(unitLbp?.toFixed(0), unitPrice)} / {unitLabel(unit, locale)}
          </span>
        </p>
      ) : null}
      {/* Why the figure is what it is: the base, the rule, the result. One
          compact line, only when a discount applies - the panel is for
          weighing, not for reading. */}
      {discount && unitLbp ? (
        <p
          className="numeric mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-control border border-burgundy-red/20 bg-burgundy-red/5 px-3 py-2 text-xs text-ink-muted"
          data-testid="weight-discount-context"
        >
          <span>
            {t.pos.weightBasePrice}:{' '}
            <span className="line-through">{formatLbp(discount.basePriceLbp, locale)}</span>
          </span>
          <span className="rounded-full bg-burgundy-red/10 px-1.5 py-0.5 font-semibold text-burgundy-red">
            {format(t.pricing.discountBadge, { percent: toDecimal(discount.percent ?? '0').toFixed(0) })}
          </span>
          <span>
            {t.pos.weightEffectivePrice}:{' '}
            <span className="font-semibold text-ink">{formatLbp(unitLbp.toFixed(0), locale)}</span> / {unitLabel(unit, locale)}
          </span>
        </p>
      ) : null}
      {/* Stock promised to unpaid orders. Nothing has been deducted, but it
          cannot be sold twice, so the cashier sees the figure that matters. */}
      {hasReservations && availability ? (
        <p className="mb-3 flex flex-wrap justify-between gap-2 rounded-control border border-antique-gold/40 bg-antique-gold/10 p-2.5 text-xs text-ink">
          <span>
            {t.reservations.onHand}{' '}
            <span className="numeric font-semibold">
              {formatQuantity(availability.onHand)} {unitLabel(unit, locale)}
            </span>
          </span>
          <span>
            <span className="numeric font-semibold text-antique-gold-deep">
              {formatQuantity(availability.reserved)}
            </span>{' '}
            {t.reservations.reservedShort}
          </span>
          <span>
            {t.reservations.available}{' '}
            <span className="numeric font-semibold text-butcher-green">
              {formatQuantity(availability.available)} {unitLabel(unit, locale)}
            </span>
          </span>
        </p>
      ) : null}

      {/* No scale here. Talking to a real scale is not implemented - it is
          parked in Settings, marked Coming Soon - and a "Connect a scale"
          button on the one dialogue a cashier uses forty times an hour is a
          button that never works. Weight is typed, and the keypad below is
          the whole of it. */}

      {/* --- unit toggle, only for weighed products --------------------- */}
      {mode === 'WEIGHT_KG' ? (
        <div
          className="mb-3 grid grid-cols-2 gap-1 rounded-control bg-surface-sunken p-1"
          role="group"
          aria-label={t.pos.enterWeight}
        >
          {(['kg', 'g'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setEntryUnit(option)}
              aria-pressed={entryUnit === option}
              className={clsx(
                'rounded-[6px] py-2 text-sm font-semibold transition-colors',
                entryUnit === option ? 'bg-surface text-butcher-green shadow-sm' : 'text-ink-muted hover:text-ink',
              )}
            >
              {option === 'kg' ? t.pos.weightKg : t.pos.weightG}
            </button>
          ))}
        </div>
      ) : null}

      {/* --- read-out ---------------------------------------------------- */}
      <div className="mb-1 rounded-card border border-line bg-surface-soft p-4 text-center">
        <p className="numeric text-3xl font-semibold text-ink" aria-live="polite">
          {entry || '0'}
          <span className="ms-1.5 text-base font-medium text-ink-muted">
            {mode === 'CUSTOM_AMOUNT' ? 'USD' : mode === 'WEIGHT_KG' ? entryUnit : unitLabel(unit, locale)}
          </span>
        </p>

        {/* The conversion, shown the moment grams are typed. */}
        {mode === 'WEIGHT_KG' && entryUnit === 'g' && entry.trim().length > 0 && check.ok ? (
          <p className="numeric mt-1 flex items-center justify-center gap-1.5 text-sm text-butcher-green">
            <Scale className="h-3.5 w-3.5" aria-hidden="true" />
            {format(t.pos.convertedTo, { kg: normalised })}
          </p>
        ) : null}

        {previewTotal ? (
          <p className="mt-2 border-t border-line pt-2 text-sm text-ink-muted">
            {t.pos.lineTotalPreview}
            <span className="numeric ms-2 font-semibold text-ink">{money(previewTotal.lbp, previewTotal.usd)}</span>
          </p>
        ) : null}
      </div>

      {problemMessage ? (
        <p className="field-error mb-2" role="alert">
          {problemMessage}
        </p>
      ) : null}

      {/* --- keypad ------------------------------------------------------ */}
      <div dir="ltr" className="mb-3 grid grid-cols-3 gap-2" data-testid="quantity-keypad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => press(key)}
            disabled={key === '.' && mode === 'COUNT'}
            className="numeric h-touch-lg rounded-control border border-line-strong bg-surface text-xl font-semibold text-ink transition-colors hover:bg-surface-sunken active:bg-surface-sunken disabled:opacity-30"
          >
            {key}
          </button>
        ))}

        <button
          type="button"
          onClick={backspace}
          className="flex h-touch-lg items-center justify-center rounded-control border border-line-strong bg-surface text-ink-muted transition-colors hover:bg-surface-sunken"
          aria-label="Delete last digit"
        >
          <Delete className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <button type="button" onClick={clearEntry} className="btn-secondary mb-4 w-full">
        {t.pos.keypadClear}
      </button>

      {/* --- note -------------------------------------------------------- */}
      <label htmlFor="line-note" className="label">
        {t.pos.lineNote}
      </label>
      <input
        id="line-note"
        type="text"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={t.pos.lineNotePlaceholder}
        maxLength={200}
        className="input"
      />
    </Modal>
  );
}
